import type { ProductServiceItem } from './helpers/verificationContext';

/**
 * Product name constants for matching. Used to identify products by name
 * when the ID-to-name mapping comes from the API (getregistrationdetails).
 */
export const PRODUCT_NAMES = {
    BANKING_PAYMENTS: 'Banking & Payments Infrastructure',
    DDA_CHECKING: 'DDA/Checking Accounts',
    CRYPTO_OTC: 'Crypto OTC',
    CRYPTO_PORTL: 'Crypto Acquiring (PORTL)',
    MERCHANT_SERVICES: 'Merchant Services',
    CARD_SERVICES: 'Card services',
    CURRENCY_EXCHANGE: 'Currency Exchange',
} as const;

/**
 * Builds ID-to-name map from fetched product services.
 */
function buildIdToNameMap(productList: ProductServiceItem[] | undefined): Map<number, string> {
    const map = new Map<number, string>();
    if (!productList) return map;
    for (const p of productList) {
        map.set(p.id, p.name);
    }
    return map;
}

/**
 * Parses productServices string (comma-separated IDs) into product IDs.
 */
export function getProductIds(productServices: string | undefined): number[] {
    if (!productServices) return [];
    return productServices
        .split(',')
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !Number.isNaN(id));
}

/**
 * Parses productServices string into product names using the fetched product list.
 */
export function getProductNames(
    productServices: string | undefined,
    productList: ProductServiceItem[] | undefined
): string[] {
    const idToName = buildIdToNameMap(productList);
    return getProductIds(productServices)
        .map((id) => idToName.get(id))
        .filter((name): name is string => !!name);
}

/**
 * Returns true when DDA/Checking Accounts is the *only* selected product.
 */
export function hasOnlyDDAProduct(
    productServices: string | undefined,
    productList: ProductServiceItem[] | undefined
): boolean {
    if (!productList?.length) return false;
    const ids = getProductIds(productServices);
    const names = getProductNames(productServices, productList);
    return ids.length === 1 && names.includes(PRODUCT_NAMES.DDA_CHECKING);
}

/**
 * Returns true when Banking & Payments Infrastructure is selected.
 */
export function hasBankingProduct(
    productServices: string | undefined,
    productList: ProductServiceItem[] | undefined
): boolean {
    if (!productList?.length) return false;
    return getProductNames(productServices, productList).includes(PRODUCT_NAMES.BANKING_PAYMENTS);
}

/**
 * Returns true when Crypto OTC is selected.
 */
export function hasCryptoOTCProduct(
    productServices: string | undefined,
    productList: ProductServiceItem[] | undefined
): boolean {
    if (!productList?.length) return false;
    return getProductNames(productServices, productList).includes(PRODUCT_NAMES.CRYPTO_OTC);
}

/**
 * Returns true when Crypto Acquiring (PORTL) is selected.
 */
export function hasPortlProduct(
    productServices: string | undefined,
    productList: ProductServiceItem[] | undefined
): boolean {
    if (!productList?.length) return false;
    return getProductNames(productServices, productList).includes(PRODUCT_NAMES.CRYPTO_PORTL);
}

/**
 * Returns true when Merchant Services is selected.
 */
export function hasMerchantProduct(
    productServices: string | undefined,
    productList: ProductServiceItem[] | undefined
): boolean {
    if (!productList?.length) return false;
    return getProductNames(productServices, productList).includes(PRODUCT_NAMES.MERCHANT_SERVICES);
}
